import { z } from 'zod';

/**
 * Zod schemas for BP-13 §5 (ARCHITECTURE §3 Validation).
 *
 * Hand-written, for the reason `features/accounting/schemas.ts` gives:
 * `lib/api/schema.d.ts` is regenerated in CI, and a screen compiled against
 * it would track whatever was last exported rather than the contract this
 * feature was written to.
 *
 * **Nothing here carries image bytes.** A photograph arrives as multipart and
 * leaves as a signed URL; base64 in a JSON body would triple its size and put
 * a client's hands in every log line that echoes a request.
 */

/** `session_photo.kind` — a sequence, not a taxonomy. */
export const photoKinds = ['before', 'after', 'detail'] as const;
export type PhotoKind = (typeof photoKinds)[number];

export const photoSchema = z.object({
  id: z.string(),
  session_id: z.string(),
  client_id: z.string(),
  kind: z.enum(photoKinds),
  taken_at: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  byte_size: z.number().int(),
  retain_until: z.string(),
  /** Rule 7: the objects are gone and the record stays. Rendered as a tombstone. */
  deleted_at: z.string().nullable().default(null),
  /**
   * Rule 2, computed per request by the api rather than stored.
   *
   * A `photo_marketing` consent withdrawn this morning turns the share button
   * off this morning, which a column written at capture time could not do.
   */
  shareable: z.boolean().default(false),
});
export type Photo = z.infer<typeof photoSchema>;
export const photoListSchema = z.array(photoSchema);

export const signedUrlSchema = z.object({
  /** Relative to the api's own origin, so it works on both profiles. */
  url: z.string(),
  expires_at: z.string(),
});
export type SignedUrl = z.infer<typeof signedUrlSchema>;
